/**
 * identity.ts — the identity/auth gateway calls.
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.7 (portal redesign).
 *
 * WHY: register/login return an opaque session token; the profile/address reads
 * are authed (Bearer token). register/login/reset use `parseError` so the
 * gateway's Zod field errors (confirmed live: `{formErrors, fieldErrors}`)
 * reach the form and render inline. Everything else stays PHI-free.
 */
import { ApiError, del, get, patch, post, queryString, request } from './client';
import type {
  Address,
  AuditListResponse,
  AuditQuery,
  AuthSession,
  ClinicianProfile,
  ClinicianRecord,
  ClinicianRecordUpdate,
  InviteCreateRequest,
  InviteCreated,
  LoginRequest,
  Me,
  PatientProfile,
  PhotoUploadGrant,
  RegisterRequest,
  ScrBlock,
  StaffUserLookupResponse,
  StaffUserSearchResult,
  StaffUsersList,
} from './types';

/** POST /v1/identity/auth/register → session token (+ optional re-entry-token assessment bind). */
export function register(body: RegisterRequest): Promise<AuthSession> {
  return post('/v1/identity/auth/register', body, { parseError: true });
}

/** POST /v1/identity/auth/login → session token. */
export function login(body: LoginRequest): Promise<AuthSession> {
  return post('/v1/identity/auth/login', body, { parseError: true });
}

/** POST /v1/identity/auth/bind-assessment — login-after-pay: email-match bind then claim. */
export function bindAssessment(body: { reEntryToken: string }): Promise<{ ok: true }> {
  return post('/v1/identity/auth/bind-assessment', body, { auth: true, parseError: true });
}

/** GET /v1/identity/auth/me → the signed-in principal. */
export function getMe(): Promise<Me> {
  return get('/v1/identity/auth/me', true);
}

/** POST /v1/identity/auth/reset/request — always 2xx (no account enumeration). */
export function requestPasswordReset(email: string): Promise<{ ok?: boolean }> {
  return post('/v1/identity/auth/reset/request', { email });
}

/** POST /v1/identity/auth/reset/confirm — set a new password from a reset token. */
export function confirmPasswordReset(token: string, password: string): Promise<{ ok?: boolean }> {
  return post('/v1/identity/auth/reset/confirm', { token, password }, { parseError: true });
}

/**
 * POST /v1/identity/auth/me/reset-link (authed, STAFF) — the signed-in
 * "Change password" self-service: identity emails a single-use reset link to
 * the CALLER'S OWN mailbox (subject = the verified session, never a body), and
 * the user sets the new password via the emailed link — the old password is
 * never typed here (unlike {@link changePassword}). Staff only: a clinician/
 * admin; the gateway routes it behind AuthGuard and identity 403s a patient.
 */
export function requestOwnPasswordReset(): Promise<{ ok?: boolean }> {
  return post('/v1/identity/auth/me/reset-link', {}, { auth: true });
}

/** POST /v1/identity/auth/verify-email — confirm an email from its token. */
export function verifyEmail(token: string): Promise<{ verified: boolean }> {
  return post('/v1/identity/auth/verify-email', { token });
}

/**
 * POST /v1/identity/auth/resend-verification — guest. Always 200 (no oracle).
 * A new 24h link is emailed only when that address exists and is still unverified.
 */
export function resendVerification(email: string): Promise<{ ok: true }> {
  return post('/v1/identity/auth/resend-verification', { email });
}

/**
 * POST /v1/identity/auth/invite/accept — a clinician/admin accepts their invite
 * by setting a password; returns a session (role comes from the invite, not the
 * caller). This is the only way a staff account is activated (R41 — no self-serve).
 */
export function acceptInvite(token: string, password: string): Promise<AuthSession> {
  return post('/v1/identity/auth/invite/accept', { token, password }, { parseError: true });
}

/** GET /v1/identity/patients/me/profile — the patient's registered details. */
export function getPatientProfile(): Promise<PatientProfile> {
  return get('/v1/identity/patients/me/profile', true);
}

/** PATCH /v1/identity/patients/me/address — update the registered address. */
export function updateRegisteredAddress(address: Address): Promise<PatientProfile> {
  return patch('/v1/identity/patients/me/address', address);
}

/**
 * PATCH /v1/identity/patients/me/name — set/update the patient's own first/last
 * name. Both are required + non-empty server-side (`patientNameUpdate`); returns
 * the refreshed profile so the page round-trips without a second read.
 */
export function updateMyName(firstName: string, lastName: string): Promise<PatientProfile> {
  return patch('/v1/identity/patients/me/name', { firstName, lastName });
}

/**
 * POST /v1/identity/auth/password — change the signed-in user's password.
 * Identity verifies the current password first (403 = wrong current) and
 * enforces the same min-10 Zod rule as registration (`parseError` so a 400
 * field error reaches the form). Success revokes every OTHER session
 * server-side — the caller's own token stays valid, so the profile page
 * tells the user their other devices are signed out.
 */
export function changePassword(currentPassword: string, newPassword: string): Promise<{ ok?: boolean }> {
  return post('/v1/identity/auth/password', { currentPassword, newPassword }, { auth: true, parseError: true });
}

/* ── Staff (CLINICIAN / ADMIN) ─────────────────────────────────────────────── */

/**
 * Why identity will not give the SCR details for a patient whose account is
 * not finished (returning patients §2.2, 2026-09-25). A closed vocabulary.
 * `not_completed`: a paid patient with no account yet. `completed_unverified`:
 * an account whose email has not been confirmed.
 */
export type ScrUnavailableReason = 'not_completed' | 'completed_unverified';

/**
 * Reads identity's SCR refusal body into a {@link ScrUnavailableReason}.
 *
 * [CONTRACTS GAP] identity's getScr 409 `{code: 'account_not_completed',
 * accountStatus}` (returning patients plan S2.3) is not in the pinned
 * contracts yet, so its shape is held here. Identity does not send it yet:
 * today a patient with no account is a 404, which clinicians/scrPanelState.ts
 * handles on its own.
 *
 * - `code` must be exactly `account_not_completed`, otherwise undefined and
 *   the 409 is treated as the fault it is.
 * - `accountStatus: 'completed_unverified'` gives the unconfirmed-email copy.
 * - Anything else under that code gives `not_completed`: absent (the design's
 *   first draft of the body had no status), `not_completed`, or a value this
 *   build does not know. The code alone already says the account is not
 *   finished, and the CTO decided that must never look like a fault, so an
 *   unknown status gets the "no account yet" copy rather than an error.
 * - Extra keys are ignored (tolerant reader, plan §2.1 rule 5): a Nest body can
 *   gain `message` or `statusCode` without breaking this.
 */
export function readScrUnavailableReason(body: unknown): ScrUnavailableReason | undefined {
  if (body === null || typeof body !== 'object' || Array.isArray(body)) return undefined;
  const { code, accountStatus } = body as { code?: unknown; accountStatus?: unknown };
  if (code !== 'account_not_completed') return undefined;
  return accountStatus === 'completed_unverified' ? 'completed_unverified' : 'not_completed';
}

/**
 * GET /v1/identity/patients/:userId/scr — the SCR quick-copy block
 * (firstName/lastName?/DOB/postcode) for the clinician review. CLINICIAN/ADMIN
 * only (a PATIENT is 403). Names may be null (not captured until v3's MPI).
 *
 * A refusal because the patient's account is not finished arrives as an
 * ApiError whose `reason` is a {@link ScrUnavailableReason}; see
 * clinicians/scrPanelState.ts for how every failure is shown.
 */
export function getScr(userId: string): Promise<ScrBlock> {
  return request(`/v1/identity/patients/${encodeURIComponent(userId)}/scr`, {
    method: 'GET',
    auth: true,
    readErrorReason: readScrUnavailableReason,
  });
}

/**
 * POST /v1/identity/admin/invites — invite a clinician/admin (ADMIN only). The
 * raw token is returned ONCE so the admin can build the invite link. Uses
 * `parseError` so a 400 (e.g. clinician invite missing fullName/registration)
 * or 409 (email already registered as a clinician) reaches the form as a
 * field/detail error.
 */
export function createInvite(body: InviteCreateRequest): Promise<InviteCreated> {
  return post('/v1/identity/admin/invites', body, { auth: true, parseError: true });
}

/**
 * GET /v1/identity/admin/users — the staff directory (ADMIN only): active
 * clinician/admin accounts plus any invites still pending acceptance. Powers
 * the Users tab table; refetched after a successful invite so the new pending
 * row appears immediately alongside the one-time link.
 */
export function getStaffUsers(): Promise<StaffUsersList> {
  return get('/v1/identity/admin/users', true);
}

/**
 * POST /v1/identity/admin/invites/resend — re-mints a fresh single-use token
 * for a pending invite's email (any prior unused invite for that email is
 * invalidated server-side). Same response shape as `createInvite`.
 */
export function resendInvite(email: string): Promise<InviteCreated> {
  return post('/v1/identity/admin/invites/resend', { email }, { auth: true, parseError: true });
}

/** DELETE /v1/identity/admin/invites/:email — revoke a pending invite. */
export function revokeInvite(email: string): Promise<{ revoked: boolean }> {
  return del(`/v1/identity/admin/invites/${encodeURIComponent(email)}`);
}

/**
 * DELETE /v1/identity/admin/users/:userId — remove an active staff account.
 * The gateway refuses this for the caller's own row and for
 * itops@thegpservice.co.uk regardless of what the client sends — the UI hides
 * the action for those rows as a courtesy, never as the real control.
 * An OPTIONAL `reason` (recorded on the identity audit row, G2) posts as a
 * `{reason}` body; when empty the request carries NO body at all (the identity
 * DELETE accepts either).
 */
export function deleteStaffUser(userId: string, reason?: string): Promise<{ deleted: boolean }> {
  const trimmed = reason?.trim();
  if (!trimmed) return del(`/v1/identity/admin/users/${encodeURIComponent(userId)}`);
  return request(`/v1/identity/admin/users/${encodeURIComponent(userId)}`, { method: 'DELETE', auth: true, body: { reason: trimmed } });
}

/**
 * A principal id that is NOT a person — the internal service callers that act
 * on the platform's behalf, which clinic-service marks with the `svc:` prefix
 * (`SERVICE_PRINCIPAL_PREFIX` in its deals.service.ts, minted by
 * orders-service's `ORDERS_SERVICE_PRINCIPAL`). These ride onto audit and
 * activity rows verbatim as `actorUserId`, so any surface that resolves actor
 * ids to names will meet one.
 *
 * The test is "carries a namespace" rather than the literal `svc:` on purpose:
 * an identity-issued uuid can never contain a colon, so this cannot drop a
 * real staff id, and it stays correct if a second namespace is ever minted.
 */
export function isServicePrincipal(id: string): boolean {
  return id.includes(':');
}

/**
 * The shape the lookup contract will actually accept — `{ userIds: z.array(uuid) }`.
 *
 * Used as the RECOVERY filter below, not the first-pass one. See
 * {@link lookupStaffUsers} for why predicting bad ids up front is the weaker
 * design.
 */
function isUuid(id: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id);
}

/**
 * POST /v1/identity/admin/users/lookup — bulk userId→email resolution
 * (ADMIN only, max 200 ids/call). Powers the "Orders & subscriptions" tab's
 * one batched patient-email lookup and the callback note trail's author
 * emails.
 *
 * SERVICE PRINCIPALS ARE DROPPED HERE, AT THE BOUNDARY (2026-08-28). The
 * request contract is `{ userIds: z.array(uuid) }` behind a ZodValidationPipe,
 * so ONE non-uuid element 400s the WHOLE batch and nobody's name resolves —
 * not just the offending id's. That is exactly what happened on the CTO's won
 * deal: its feed carries `svc:gpsclinics-orders-service`, that id rode into
 * the batch, and every row in the drawer silently lost its name, the genuine
 * human admin included. Filtering at the boundary rather than in one caller
 * means all four call sites (the deal drawer, the ADMIN audit tracer, and the
 * two display-map helpers below) are safe by construction, including any added
 * later. A service principal has no staff record to resolve, so nothing is
 * lost: those rows already render correctly as the role chip alone.
 */
export async function lookupStaffUsers(userIds: string[]): Promise<StaffUserLookupResponse> {
  const resolvable = userIds.filter((id) => !isServicePrincipal(id));
  // `.min(1)` on the request contract — an all-service batch must not be
  // posted as an empty array; there is simply nothing to ask.
  if (resolvable.length === 0) return { users: [] };
  try {
    return await post('/v1/identity/admin/users/lookup', { userIds: resolvable }, { auth: true });
  } catch (error) {
    // ── RECOVERY: ONE BAD ID MUST NOT COST EVERY NAME (2026-08-29) ──────────
    //
    // The namespaced filter above is a PREDICTION, and predictions about id
    // shapes keep being wrong. It was added for `svc:…` and does not catch the
    // bare service actors clinic-service also writes (`internal-cron`,
    // `orders-service` — both real values on its audit rows). The drawer sends
    // EVERY actorUserId it finds and relies entirely on this boundary, so any
    // id nobody anticipated 400s the whole batch and the drawer loses every
    // name at once, the genuine human admin included.
    //
    // Rather than guess harder, recover: a 400 means the contract rejected the
    // set, so retry with only the ids the contract can accept. That fixes the
    // whole class — including the next id shape somebody invents — instead of
    // the one instance in front of us. Anything other than a 400 is a real
    // outage and is rethrown for the caller's own error handling.
    if (!(error instanceof ApiError) || error.status !== 400) throw error;
    const strict = resolvable.filter(isUuid);
    // Nothing person-shaped left, or the strict set is what we already sent —
    // there is no second request worth making.
    if (strict.length === 0 || strict.length === resolvable.length) return { users: [] };
    return post('/v1/identity/admin/users/lookup', { userIds: strict }, { auth: true });
  }
}

/**
 * The same batched lookup flattened to a userId→email display map — the shape
 * the Analytics/Earnings tables key their name columns on. Best-effort by
 * convention: callers `.catch(() => undefined)` and show the raw id (never
 * PHI — the ref is a UUID) when the lookup is down.
 */
export async function lookupStaffEmailMap(userIds: string[]): Promise<Record<string, string>> {
  if (userIds.length === 0) return {};
  const { users } = await lookupStaffUsers(userIds);
  return Object.fromEntries(users.map((u) => [u.userId, u.email]));
}

/**
 * The batched lookup flattened to a userId→DISPLAY-NAME map: the stored full
 * name when identity has one, the email otherwise (Wave-2 "name, not email";
 * `fullName` on the lookup response is additive, 2026-07-21). Powers the
 * Payments card/list patient labels. Best-effort by the same convention as
 * {@link lookupStaffEmailMap}: callers catch and fall back to the raw id.
 */
export async function lookupStaffDisplayMap(userIds: string[]): Promise<Record<string, string>> {
  if (userIds.length === 0) return {};
  const { users } = await lookupStaffUsers(userIds);
  return Object.fromEntries(users.map((u) => [u.userId, u.fullName?.trim() || u.email]));
}

/**
 * GET /v1/identity/clinician/me/profile — the signed-in clinician's own
 * profile (UX Wave 8: name, registration number, prescriber type, designation,
 * bio, and short-lived signed READ URLs for the photo + signature images).
 * A caller with no clinician record (e.g. the master admin) gets a 404 — the
 * "Your profile" panel treats that as "nothing to show" and
 * hides quietly rather than rendering an error.
 */
export function getClinicianProfile(): Promise<ClinicianProfile> {
  return get('/v1/identity/clinician/me/profile', true);
}

/**
 * POST /v1/identity/clinician/me/signature-upload — request a signed PUT grant
 * for the clinician's prescription signature (or the `{stub: true}` no-op grant
 * when object storage isn't configured on this environment). Mirrors the
 * journey's photo-upload grant shape/flow (see api/journey.ts).
 */
export function requestSignatureUpload(): Promise<PhotoUploadGrant> {
  return post('/v1/identity/clinician/me/signature-upload', {}, { auth: true });
}

/** PUT the signature image to the signed URL. A stub grant resolves immediately. */
export async function uploadSignature(grant: PhotoUploadGrant, file: File): Promise<void> {
  if (grant.stub || !grant.uploadUrl) return;
  const response = await fetch(grant.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    body: file,
  });
  if (!response.ok) throw new Error(`Signature upload failed (${response.status})`);
}

/**
 * PUT the DRAWN signature PNG to the signed URL. The grant pins the object's
 * contentType to image/png server-side, so the header is ALWAYS the literal
 * 'image/png' — never `blob.type` — a mismatch is a signed-URL 403. A stub
 * grant resolves immediately (nothing to upload).
 */
export async function uploadSignaturePng(grant: PhotoUploadGrant, blob: Blob): Promise<void> {
  if (grant.stub || !grant.uploadUrl) return;
  const response = await fetch(grant.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': 'image/png' },
    body: blob,
  });
  if (!response.ok) throw new Error(`Signature upload failed (${response.status})`);
}

/**
 * POST /v1/identity/clinician/me/signature-confirm {objectRef} — bind a
 * completed signature PUT to the clinician record. The upload grant NO LONGER
 * stamps signatureRef server-side (identity change, this batch): a grant that
 * was never PUT to must never flip the profile, so the client confirms only
 * after a 2xx PUT. Returns the refreshed profile (signatureSet true).
 */
export function confirmSignatureUpload(objectRef: string): Promise<ClinicianProfile> {
  return post('/v1/identity/clinician/me/signature-confirm', { objectRef }, { auth: true });
}

/**
 * POST /v1/identity/admin/users/search — EXACT account-email match (ADMIN,
 * v0.6.5). Returns `{user: null}` on no match (never an error — the callback
 * patient-context panel renders "no account found" for that). POST (not a GET
 * query string) so the searched email never lands in a URL/access log (R19).
 */
export function searchStaffUserByEmail(email: string): Promise<StaffUserSearchResult> {
  return post('/v1/identity/admin/users/search', { email }, { auth: true });
}

/**
 * GET /v1/identity/clinician/records/:userId — a clinician's display record
 * (v0.6.5), used to resolve a prescription's `issuedBy` ref into a prescriber
 * name. Callers fall back to rendering the raw ref when this 404s (older
 * prescriptions may carry a free-text name rather than a userId).
 */
export function getClinicianRecord(userId: string): Promise<ClinicianRecord> {
  return get(`/v1/identity/clinician/records/${encodeURIComponent(userId)}`, true);
}

/**
 * POST /v1/identity/admin/users/:userId/reset-link (ADMIN) — mint a one-time
 * password-reset link for an ACTIVE staff account. Identity ALSO emails the
 * user; the Users tab shows the returned link as a copy fallback. Staff only:
 * the gateway 400s for a patient userId (the UI only offers this on staff rows,
 * so that path is never hit). Type imported inline to keep this file
 * append-only (a sibling agent edits the same module in a parallel worktree).
 */
export function createStaffResetLink(userId: string): Promise<import('./types').StaffResetLink> {
  return post(`/v1/identity/admin/users/${encodeURIComponent(userId)}/reset-link`, {}, { auth: true });
}

/* ── G2 clinical governance: audit read + clinician record management ─────── */

/**
 * GET /v1/identity/admin/audit?limit&before → the identity-service audit
 * trail (ADMIN, G2): invites created/resent/revoked, user deletions, reset
 * links issued, clinician-record updates — newest first. Carries the Wave-3c
 * tracer filters (actorUserId / action / targetType / from / to) as strict,
 * identity-vocabulary comma-lists (admin/auditVocab.ts).
 */
export function getIdentityAudit(params: AuditQuery = {}): Promise<AuditListResponse> {
  return get(`/v1/identity/admin/audit${queryString(params)}`, true);
}

/**
 * PATCH /v1/identity/clinician/me/profile — the signed-in clinician updates
 * their OWN display identity (G2; UX Wave 8: firstName+lastName — identity
 * derives fullName "First Last" — designation, bio, and the photo confirm
 * `photoRef` after a 2xx signed PUT). Registration number / prescriber type
 * are NOT self-serve (prescribing identity — admin-only via
 * {@link updateClinicianRecord}). Returns the refreshed profile.
 */
export function updateMyClinicianProfile(body: import('./types').ClinicianProfileUpdate): Promise<ClinicianProfile> {
  return patch('/v1/identity/clinician/me/profile', body);
}

/**
 * PATCH /v1/identity/admin/users/:userId/clinician-record — an ADMIN edits a
 * clinician's record (fullName / registrationNumber / prescriberType, all
 * optional, G2). The optional `reason` lands on the identity audit row —
 * never in an event/log (R19). Returns the refreshed display record.
 */
export function updateClinicianRecord(userId: string, body: ClinicianRecordUpdate): Promise<ClinicianRecord> {
  return patch(`/v1/identity/admin/users/${encodeURIComponent(userId)}/clinician-record`, body);
}

/* ── UX Wave 6 §3: payments-surface patient contacts ──────────────────────── */

/** One resolved patient/staff contact from the batched lookup (Wave 6 §3). */
export interface StaffContact {
  email: string;
  fullName: string | null;
}

/**
 * The batched lookup flattened to a userId→CONTACT map — BOTH the stored full
 * name (null when identity has none) and the email, so the Payments board
 * cards/list rows can render "name headline, email secondary" (Wave 6 §3)
 * from ONE call. Same best-effort convention as {@link lookupStaffEmailMap}:
 * callers catch and fall back to the raw id. Appended (this module is
 * append-only by convention — sibling agents edit it in parallel worktrees).
 */
export async function lookupStaffContactMap(userIds: string[]): Promise<Record<string, StaffContact>> {
  if (userIds.length === 0) return {};
  const { users } = await lookupStaffUsers(userIds);
  return Object.fromEntries(users.map((u) => [u.userId, { email: u.email, fullName: u.fullName?.trim() || null }]));
}

/* ── UX Wave 6 §5: the PMS patient directory ──────────────────────────────── */

/**
 * GET /v1/identity/admin/patients — the paginated, searchable, newest-first
 * PATIENT directory (UX Wave 6 §5 — the HubSpot-contacts equivalent). Staff-
 * wide: ADMIN **and** CLINICIAN receive the IDENTICAL money-free shape
 * (identity fields only). `limit` 1..100 (default 25), `before` = a
 * strictly-older createdAt cursor, `q` = case-insensitive email/name search —
 * `queryString` drops an empty `q`, matching the strict boundary's rule that
 * a blank search is omitted, never sent. R19 note: `q` rides an authed staff
 * GET the services never log; the response body is the only place the
 * emails/names appear. Appended (this module is append-only by convention).
 */
export function listPatients(
  params: import('./types').AdminPatientListQuery = {},
): Promise<import('./types').AdminPatientListResponse> {
  return get(`/v1/identity/admin/patients${queryString(params)}`, true);
}

/**
 * POST /v1/identity/admin/patients/:userId/mark-test — flag (or, with
 * `{revert:true}`, un-flag) a PATIENT account as a QA/rehearsal record so it
 * stops sitting in the directory beside real patients (identity #86,
 * 2026-09-04).
 *
 * ⚠️ IT HIDES; IT DELETES NOTHING. One boolean is written server-side. The
 * account, its credential, its sessions, its append-only consent rows, its
 * audit trail and every clinical record in every other service are untouched —
 * erasure (UK GDPR Art. 17) is a different act with its own lawful basis and
 * its own lane. The screens that touch the flag say exactly that in words —
 * the filter hint in `pms/PatientsFilters.tsx` and the deal dialog in
 * `pipeline/DealDrawer.tsx` — because "mark as test" is the kind of phrase a
 * member of staff can reasonably read as "get rid of this".
 *
 * ⚠️ ADMIN ONLY — NARROWER THAN THE DIRECTORY READ ABOVE, AND NARROWER THAN THE
 * PIPELINE'S OWN MARK (which either staff role may use on a DEAL). identity's
 * `@Roles('ADMIN')` is the control (R41); the portal only mirrors it. The
 * asymmetry is deliberate and worth keeping straight: a deal is one journey, and
 * closing a rehearsal journey harms nobody, whereas this flag hides a HUMAN from
 * the list a clinician searches to FIND patients — so a mis-mark makes a real
 * patient invisible to the next clinician looking.
 *
 * `parseError` because the refusals are the useful part and they are all
 * PHI-free by construction (identity returns ids and a state sentence): a
 * non-PATIENT target is 400 ("the column is PATIENT-only"), an unknown or
 * foreign-tenant id is 404, and a generic house message in place of either
 * would leave an admin re-pressing a control that can never work.
 */
/**
 * ⚠️ THE ROUTINE CALLER IS THE DEAL, NOT THIS — AND THE CASCADE THAT MAKES
 * THAT TRUE IS ON `dev` NOW (verified 2026-09-05).
 *
 * TENSE MATTERS HERE, AND IT MATTERS IN BOTH DIRECTIONS. This block used to
 * say "the cascade is not merged to `dev`", which was true when written and
 * went stale the moment it landed. Re-grounded against the repos: clinic-
 * service `dev` = 9fb728c, orders-service `dev` = 46784b3, identity-service
 * `dev` = fdc9d21. `POST /v1/deals/:id/mark-test` on clinic now marks the
 * DEAL, its PLAN (`OrderCase.isTest`, via orders' `POST /v1/internal/order-
 * cases/:id/test-record`) and its PATIENT (`User.isTest`, via THIS route with
 * the synthetic `svc:` ADMIN principal) in one action, and `{revert: true}`
 * clears all three — orders audits the reversal as `order_unmarked_test`.
 *
 * ⚠️ THE PORTAL CALLS THIS IN THE UNDO DIRECTION ONLY. The single caller is
 * `pms/PatientRecordDrawer.tsx` (ADMIN only), and it always sends
 * `{revert: true}`:
 *   - the UN-MARK is offered UNCONDITIONALLY for a flagged patient. Its
 *     original justification ("nothing else can clear this flag until the
 *     cascade ships") HAS EXPIRED — the deal route clears the flag today. It
 *     stays on three grounds that have not, all three checked against clinic
 *     `dev` (`DealsService.resolvePatientLeg`):
 *       1. THE CASCADE DELIBERATELY WITHHOLDS THE HUMAN LEG. It moves
 *          `User.isTest` only when every OTHER deal of that patient in the
 *          tenant already agrees with where it is going — a clinical-safety
 *          rule, so that marking one rehearsal journey cannot make a real
 *          patient vanish from the directory a clinician searches. The MIRROR
 *          holds on the way back: it refuses to clear the human flag while the
 *          patient's other journeys are still test records. That cohort cannot
 *          be un-flagged by the deal route at all, in either direction.
 *       2. A PATIENT WITH NO DEAL IS UNREACHABLE BY IT. No card to start from,
 *          and the leg resolves nothing for a deal with no assessment or no
 *          `patientUserId` anyway.
 *       3. AN OPERATOR MUST ALWAYS BE ABLE TO UNDO A MISTAKE — and this
 *          mistake hides a HUMAN from the list clinicians search to FIND
 *          patients.
 *   - there is NO MARK on the Patients screen. One was added on 2026-09-05
 *     gated on "this patient has no deals" and removed the same day: that
 *     cohort was inferred from the board's LANE-PAGED deals read
 *     (`perLane=25`, filtered client-side by email), where "no deals matched"
 *     also means "their deal is older than the 25th in its lane". It would
 *     have offered the mark over established patients and written this flag
 *     while their deal and order case stayed counted. The full write-up is on
 *     `TestRecordAction`, including the server read that would close the gap
 *     (a deals-by-patient read on clinic-service).
 *
 * Do NOT widen this back into a general "mark as test" on a staff screen: the
 * three independent marks it replaced wrote different halves of one record.
 */
export function markPatientTest(
  userId: string,
  body: import('./types').AdminPatientMarkTestRequest = {},
): Promise<import('./types').AdminPatientMarkTestResult> {
  return post(`/v1/identity/admin/patients/${encodeURIComponent(userId)}/mark-test`, body, {
    auth: true,
    parseError: true,
  });
}

/* ── UX Wave 8: the clinician profile photo (avatar) ──────────────────────── */

/**
 * POST /v1/identity/clinician/me/photo-upload — request a signed PUT grant for
 * the clinician's OWN profile photo (UX Wave 8; or the `{stub: true}` no-op
 * grant when object storage isn't configured). The grant pins the declared
 * image content type into the V4 signature, so the caller passes the file's
 * type (jpeg/png only — identity 400s anything else). The grant NEVER stamps
 * the record: after a 2xx PUT the client confirms by PATCHing `{photoRef}`
 * via {@link updateMyClinicianProfile}. Appended (this module is append-only
 * by convention — sibling agents edit it in parallel worktrees).
 */
export function requestPhotoUpload(contentType: 'image/jpeg' | 'image/png'): Promise<PhotoUploadGrant> {
  return post('/v1/identity/clinician/me/photo-upload', { contentType }, { auth: true });
}

/**
 * PUT the profile-photo file to the signed URL with the SAME content type the
 * grant pinned (a mismatch is a signed-URL 403). A stub grant resolves
 * immediately (nothing to upload).
 */
export async function uploadProfilePhoto(
  grant: PhotoUploadGrant,
  file: Blob,
  contentType: 'image/jpeg' | 'image/png',
): Promise<void> {
  if (grant.stub || !grant.uploadUrl) return;
  const response = await fetch(grant.uploadUrl, {
    method: 'PUT',
    headers: { 'Content-Type': contentType },
    body: file,
  });
  if (!response.ok) throw new Error(`Photo upload failed (${response.status})`);
}

/* ── The ADMIN patient password-reset lane (2026-08-07) ────────────────────── */

/**
 * POST /v1/identity/admin/users/:userId/reset-email (ADMIN) — have identity
 * EMAIL a password-reset link to a PATIENT, keyed by their userId.
 *
 * WHY this and not the two lanes that already exist:
 *  - {@link createStaffResetLink} (`…/reset-link`) hands the caller a usable
 *    link. That is right for a staff account and wrong for a patient: an admin
 *    holding a patient's reset link is an account-takeover path, so identity
 *    400s a patient target there. This route returns `{ok:true}` and NEVER a
 *    link — there is nothing for the UI to copy, by construction.
 *  - {@link requestPasswordReset} (the PUBLIC lane the PMS drawer used to call)
 *    is keyed by EMAIL and is deliberately unauthenticated and enumeration-safe,
 *    which means it always 2xxs and the audit row records nobody. Keyed by
 *    userId behind an ADMIN session, identity can record WHICH admin triggered
 *    the reset — the actor a staff-initiated credential change must name.
 */
export function adminResetPatientPassword(userId: string): Promise<{ ok: boolean }> {
  return post(`/v1/identity/admin/users/${encodeURIComponent(userId)}/reset-email`, {}, { auth: true });
}

/* ── Clinics-in-Core (Clinics ADR-1, 2026-09-07): the GPS Core → Clinics sign-in handoff ── */

/**
 * POST /v1/identity/auth/exchange — trade a GPS Core sign-in token for an
 * ordinary Clinics session (`AuthSession`, the same shape login returns). A
 * GUEST lane: no bearer is sent, the Core token IS the credential and it travels
 * in the body only. NO `parseError`: /auth-core renders the same copy for every
 * refusal except a 403 (no Clinics account behind this Core sign-in yet), which
 * it tells apart by STATUS alone — no response body is ever read, so nothing
 * here can turn the page into an oracle. Appended (append-only module).
 */
export function exchangeCoreToken(body: { idToken: string }): Promise<AuthSession> {
  return post('/v1/identity/auth/exchange', body);
}
